import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  TrendingUp, Sparkles, Loader2, Send, Play, Heart, Star, Users, DollarSign,
  Tag, Repeat, Package, Rocket, BarChart3, MessageSquare, Bot, Mail, Check, Clock, Eye,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Spinner, EmptyState } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const ADVICE = [
  ["pricing", "Pricing", Tag, "text-emerald-500"],
  ["retention", "Retention", Repeat, "text-sky-500"],
  ["conversion", "Conversion", DollarSign, "text-amber-500"],
  ["packaging", "Packaging", Package, "text-violet-500"],
  ["growth", "Growth", Rocket, "text-rose-500"],
];
const SUGGESTED = [
  "How can I boost my conversion rate?",
  "What should I price my next level pack at?",
  "How do I get more players to finish my games?",
  "Which of my games should I focus on?",
];

export default function GrowthAssistant() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get("/growth/overview").then((r) => setData(r.data)).catch((e) => {
      setError(true); toast.error(formatApiError(e.response?.data?.detail));
    });
  }, []);

  if (error) return <EmptyState emoji="📈" title="Growth Assistant" text="This is available for creator accounts. Switch to a creator profile to analyse your games." />;
  if (!data) return <Spinner />;

  const p = data.portfolio;
  const advice = data.advice || {};

  return (
    <div className="p-5 lg:p-8 max-w-6xl mx-auto" data-testid="growth-page">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 grid place-items-center"><TrendingUp className="w-6 h-6 text-white" /></div>
        <div>
          <h1 className="font-display text-3xl font-extrabold">AI Growth Producer</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Your personal producer — analysing your catalogue and coaching your next move.</p>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6" data-testid="growth-kpis">
        <Kpi icon={Play} label="Total plays" value={p.total_plays.toLocaleString()} />
        <Kpi icon={Users} label="Unique players" value={p.total_players.toLocaleString()} />
        <Kpi icon={DollarSign} label="Est. revenue" value={`$${p.est_game_revenue_usd.toFixed(2)}`} />
        <Kpi icon={Repeat} label="Conversion" value={`${p.overall_conversion_pct}%`} />
        <Kpi icon={Rocket} label="Published" value={`${p.published}/${p.games}`} />
        <Kpi icon={Heart} label="Likes" value={p.total_likes.toLocaleString()} />
        <Kpi icon={Star} label="Avg rating" value={p.avg_rating || "—"} />
        <Kpi icon={Users} label="Followers" value={p.followers.toLocaleString()} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        {/* Advice + top games */}
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
            <h2 className="font-display font-bold flex items-center gap-2 mb-1"><Sparkles className="w-5 h-5 text-emerald-500" /> Producer's read</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="growth-summary">{advice.summary || "Publish and share a few games and I'll start tailoring advice to your numbers."}</p>
            <div className="grid sm:grid-cols-2 gap-3 mt-4" data-testid="growth-advice">
              {ADVICE.map(([key, label, Icon, color]) => (advice[key]?.length > 0 ? (
                <div key={key} className="rounded-xl border border-[hsl(var(--border))] p-3.5">
                  <div className={`text-sm font-bold flex items-center gap-1.5 mb-2 ${color}`}><Icon className="w-4 h-4" /> {label}</div>
                  <ul className="space-y-1.5">
                    {advice[key].map((tip, i) => <li key={i} className="text-xs text-[hsl(var(--muted-foreground))] flex gap-1.5"><span className="text-[hsl(var(--primary))]">→</span> {tip}</li>)}
                  </ul>
                </div>
              ) : null))}
            </div>
          </div>

          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
            <h2 className="font-display font-bold flex items-center gap-2 mb-3"><BarChart3 className="w-5 h-5 text-[hsl(var(--primary))]" /> Your games</h2>
            {p.top_games.length === 0 ? (
              <p className="text-sm text-[hsl(var(--muted-foreground))]">No games yet — create your first to start tracking performance.</p>
            ) : (
              <div className="space-y-2" data-testid="growth-games">
                {p.top_games.map((g) => (
                  <button key={g.id} onClick={() => navigate(`/app/build/${g.id}`)} className="w-full flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] p-3 text-left card-hover" data-testid={`growth-game-${g.id}`}>
                    <span className="text-2xl">{g.cover_emoji}</span>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{g.title}</div>
                      <div className="text-[11px] text-[hsl(var(--muted-foreground))] flex flex-wrap gap-x-3">
                        <span><Play className="w-3 h-3 inline" /> {g.plays}</span>
                        <span><Users className="w-3 h-3 inline" /> {g.players}</span>
                        <span><Star className="w-3 h-3 inline" /> {g.rating || "—"}</span>
                        {g.monetized && <span className="text-emerald-500">{g.conversion_pct}% conv · ${g.est_revenue}</span>}
                      </div>
                    </div>
                    <Badge variant={g.status === "published" ? "secondary" : "outline"} className="text-[10px] capitalize shrink-0">{g.status}</Badge>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Chat */}
        <div className="space-y-6">
          <DigestCard />
          <GrowthChat />
        </div>
      </div>
    </div>
  );
}

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function DigestCard() {
  const [enabled, setEnabled] = useState(false);
  const [configured, setConfigured] = useState(true);
  const [sending, setSending] = useState(false);
  const [day, setDay] = useState(0);
  const [hour, setHour] = useState(9);
  const [suggestion, setSuggestion] = useState(null);
  const [autoAlign, setAutoAlign] = useState(false);
  const [sections, setSections] = useState(["kpis", "top_game", "tip", "alerts"]);

  const loadPrefs = () => api.get("/growth/digest/prefs").then((r) => {
    setEnabled(r.data.enabled); setConfigured(r.data.email_configured);
    setDay(r.data.day ?? 0); setHour(r.data.hour ?? 9);
    setSuggestion(r.data.suggestion || null); setAutoAlign(!!r.data.auto_align);
    setSections(r.data.sections || ["kpis", "top_game", "tip", "alerts"]);
  });

  useEffect(() => { loadPrefs().catch(() => {}); }, []);

  const save = async (next) => {
    const payload = { enabled, day, hour, auto_align: autoAlign, sections, ...next };
    try { await api.post("/growth/digest/prefs", payload); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; }
    return true;
  };

  const toggleSection = async (key) => {
    const next = sections.includes(key) ? sections.filter((s) => s !== key) : [...sections, key];
    if (next.length === 0) { toast.error("Keep at least one section in your email."); return; }
    setSections(next);
    setPreviewHtml(null);  // preview will rebuild with the new sections next time it opens
    await save({ sections: next });
  };

  const toggle = async (v) => {
    setEnabled(v);
    if (await save({ enabled: v })) toast.success(v ? "Weekly digest on 📬" : "Weekly digest off");
    else setEnabled(!v);
  };
  const changeDay = async (v) => { setDay(v); if (await save({ day: v })) toast.success(`Digest set for ${DAYS[v]}s 📅`); };
  const changeHour = async (v) => { setHour(v); if (await save({ hour: v })) toast.success("Delivery time updated ⏰"); };
  const applySuggestion = async () => {
    if (!suggestion) return;
    setDay(suggestion.day); setHour(suggestion.hour);
    if (await save({ day: suggestion.day, hour: suggestion.hour })) toast.success("Matched to your players' peak time ⏰");
  };
  const toggleAutoAlign = async (v) => {
    setAutoAlign(v);
    if (await save({ auto_align: v })) {
      await loadPrefs().catch(() => {});   // backend realigns day/hour when turning on
      toast.success(v ? "Auto-align on — we'll follow your players' peak time ⏰" : "Auto-align off");
    } else setAutoAlign(!v);
  };
  const isAtSuggestion = suggestion && day === suggestion.day && hour === suggestion.hour;

  const sendPreview = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/growth/digest/send");
      toast.success(data.email?.sent ? "Digest sent to your email! 📈" : "Digest generated — add a Resend key to deliver real emails.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const openPreview = async () => {
    setPreviewOpen(true);
    if (previewHtml) return;
    setPreviewLoading(true);
    try {
      const { data } = await api.get("/growth/digest");
      setPreviewHtml(data.html || "<p>Preview unavailable</p>");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setPreviewOpen(false); }
    finally { setPreviewLoading(false); }
  };

  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5" data-testid="digest-card">
      <div className="flex items-center gap-2 mb-1"><Mail className="w-5 h-5 text-[hsl(var(--primary))]" /> <span className="font-bold">Weekly Producer digest</span></div>
      <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">Get a weekly email with your KPI changes and one tailored growth tip.</p>
      <div className="flex items-center justify-between rounded-xl bg-[hsl(var(--secondary))]/40 px-3 py-2.5">
        <span className="text-sm font-medium">Email me every week</span>
        <Switch checked={enabled} onCheckedChange={toggle} data-testid="digest-toggle" />
      </div>

      {enabled && (
        <div className="grid grid-cols-2 gap-2 mt-3" data-testid="digest-schedule">
          <label className="block">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Day</span>
            <select value={day} onChange={(e) => changeDay(Number(e.target.value))} disabled={autoAlign} data-testid="digest-day-select"
              className="mt-1 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-2.5 py-2 text-sm disabled:opacity-50">
              {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Time (UTC)</span>
            <select value={hour} onChange={(e) => changeHour(Number(e.target.value))} disabled={autoAlign} data-testid="digest-hour-select"
              className="mt-1 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-2.5 py-2 text-sm disabled:opacity-50">
              {Array.from({ length: 24 }, (_, h) => {
                const label = `${String(h).padStart(2, "0")}:00`;
                return <option key={h} value={h}>{label}</option>;
              })}
            </select>
          </label>
        </div>
      )}

      {enabled && suggestion && (
        <div className="mt-2 flex items-center gap-2 rounded-xl border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 px-3 py-2" data-testid="digest-suggestion">
          <Sparkles className="w-4 h-4 text-[hsl(var(--primary))] shrink-0" />
          <p className="text-[11px] leading-snug flex-1 min-w-0">
            Your players are most active on <b>{DAYS[suggestion.day]}s</b> around <b>{String(suggestion.hour).padStart(2, "0")}:00 UTC</b>.
          </p>
          {!autoAlign && !isAtSuggestion && (
            <button onClick={applySuggestion} className="text-[11px] font-bold text-[hsl(var(--primary))] hover:underline shrink-0" data-testid="digest-use-suggestion">Use this time</button>
          )}
        </div>
      )}

      {enabled && suggestion && (
        <div className="mt-2 flex items-center justify-between rounded-xl bg-[hsl(var(--secondary))]/40 px-3 py-2.5" data-testid="digest-auto-align-row">
          <span className="text-sm font-medium flex items-center gap-1.5"><Clock className="w-4 h-4 text-[hsl(var(--primary))]" /> Auto-align to peak time</span>
          <Switch checked={autoAlign} onCheckedChange={toggleAutoAlign} data-testid="digest-auto-align-toggle" />
        </div>
      )}

      {enabled && (
        <div className="mt-3" data-testid="digest-sections">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1.5">What's in your email</div>
          <div className="flex flex-wrap gap-1.5">
            {[["kpis", "KPIs"], ["top_game", "Top game"], ["tip", "Producer tip"], ["alerts", "Rating alerts"]].map(([key, label]) => {
              const on = sections.includes(key);
              return (
                <button key={key} onClick={() => toggleSection(key)} data-testid={`digest-section-${key}`}
                  className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${on ? "border-[hsl(var(--primary))]/50 bg-[hsl(var(--primary))]/10 text-[hsl(var(--foreground))]" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"}`}>
                  {on ? <Check className="w-3 h-3 text-[hsl(var(--primary))]" /> : <span className="w-3 h-3 rounded-full border border-[hsl(var(--border))] inline-block" />}
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <Button variant="outline" className="rounded-full w-full mt-3" onClick={sendPreview} disabled={sending} data-testid="digest-send-preview">
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send me a preview now
      </Button>

      <Button variant="ghost" className="rounded-full w-full mt-2 text-xs" onClick={openPreview} data-testid="digest-preview-toggle">
        <Eye className="w-4 h-4" /> Preview the exact email
      </Button>
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-[640px]" data-testid="digest-preview">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2"><Mail className="w-4 h-4 text-[hsl(var(--primary))]" /> Your weekly digest email</DialogTitle>
          </DialogHeader>
          {previewLoading ? (
            <div className="h-[520px] grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--muted-foreground))]" /></div>
          ) : (
            <>
              <iframe title="Weekly digest email preview" srcDoc={previewHtml}
                className="w-full h-[520px] rounded-lg border border-[hsl(var(--border))] bg-[#0b0d12]"
                sandbox="allow-same-origin" data-testid="digest-preview-frame" />
              <Button className="rounded-full w-full mt-1" onClick={sendPreview} disabled={sending} data-testid="digest-preview-send">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send this exact email to me
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
      {!configured && <p className="text-[11px] text-amber-500 mt-2">⚠️ Email delivery isn't configured yet — previews generate but won't be delivered until a Resend API key is added.</p>}
    </div>
  );
}

function GrowthChat() {
  const [messages, setMessages] = useState([{ role: "assistant", text: "Hi! I'm your AI Growth Producer 📈 Ask me anything about growing, pricing or monetising your games." }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length, busy]);

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || busy) return;
    const next = [...messages, { role: "user", text: q }];
    setMessages(next); setInput(""); setBusy(true);
    try {
      const { data } = await api.post("/growth/ask", { message: q, history: next.slice(-9, -1) });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "Sorry, I hit a snag analysing that. Try again in a moment." }]);
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] flex flex-col h-[560px] sticky top-20" data-testid="growth-chat">
      <div className="p-4 border-b border-[hsl(var(--border))] flex items-center gap-2">
        <Bot className="w-5 h-5 text-emerald-500" /> <span className="font-bold">Ask the Producer</span>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="growth-chat-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[88%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--secondary))]"}`}>{m.text}</div>
          </div>
        ))}
        {busy && <div className="flex justify-start"><div className="bg-[hsl(var(--secondary))] rounded-2xl px-3.5 py-2"><Loader2 className="w-4 h-4 animate-spin" /></div></div>}
        <div ref={endRef} />
      </div>
      {messages.length <= 1 && (
        <div className="px-4 pb-2 flex flex-wrap gap-1.5">
          {SUGGESTED.map((s) => <button key={s} onClick={() => send(s)} className="text-[11px] px-2 py-1 rounded-full bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--primary))]/20 text-left">{s}</button>)}
        </div>
      )}
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-[hsl(var(--border))] flex gap-2">
        <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about growth…" disabled={busy} data-testid="growth-chat-input" />
        <Button type="submit" size="icon" className="rounded-full shrink-0" disabled={busy} data-testid="growth-chat-send"><Send className="w-4 h-4" /></Button>
      </form>
    </div>
  );
}

const Kpi = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3.5">
    <div className="flex items-center gap-1.5 text-[11px] text-[hsl(var(--muted-foreground))]"><Icon className="w-3.5 h-3.5" /> {label}</div>
    <div className="text-2xl font-extrabold mt-1 tabular-nums">{value}</div>
  </div>
);
